/**
 * Schulungen (/schulungen, G-5…G-8): zwei Tabs —
 * Katalog (Kurs-CRUD, Gate schulung_schreiben = admin + personal:pflege) und
 * Fälligkeiten (Filter expiringWithin default 90 Tage, Ampel-Badge G-7,
 * „Erneuern" G-8 → Folgedatensatz direkt im Bearbeiten-Dialog, Nachweis-Upload).
 */

import { useMemo, useRef, useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { FileCheck2, Paperclip, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { EntityFormSheet, type FieldDef, type FormValues } from "@/components/admin/EntityFormSheet"
import { MasterDataSection, type Column } from "@/components/admin/MasterDataSection"
import { useMasterData } from "@/components/admin/useMasterData"
import { DueBadge, LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { fmtDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { cn } from "@/lib/utils"
import { api, apiError } from "@/services/api"
import type {
  PruefungStatus,
  TrainingCourse,
  TrainingCourseCreate,
  TrainingRecord,
} from "@/types"

const EXAM_STATUS: Record<PruefungStatus, { label: string; badge: string }> = {
  GEPRUEFT: { label: "Geprüft", badge: "border-green-300 bg-green-100 text-green-800" },
  FEHLT: { label: "Fehlt", badge: "border-yellow-300 bg-yellow-100 text-yellow-800" },
}

// ---------------------------------------------------------------------------
// Tab 1: Schulungskatalog
// ---------------------------------------------------------------------------

function CatalogTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const crud = useMasterData<TrainingCourse, TrainingCourseCreate>(
    "training-courses",
    api.trainings.courses,
    "Schulung"
  )

  const columns: Column<TrainingCourse>[] = [
    { header: "Name", cell: (c) => <span className="font-medium">{c.name}</span> },
    {
      header: "Intervall",
      className: "tabular-nums",
      cell: (c) => (c.intervalYears != null ? `${c.intervalYears} Jahre` : "—"),
    },
    {
      header: "URL",
      cell: (c) =>
        c.url ? (
          <a
            href={c.url}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline-offset-2 hover:underline"
          >
            Link
          </a>
        ) : (
          "—"
        ),
    },
    { header: "Inhalt-Datei", cell: (c) => c.contentFile ?? "—" },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "intervalYears", label: "Intervall (Jahre)", kind: "number" },
    { key: "url", label: "URL", kind: "text", placeholder: "https://…" },
    { key: "contentFile", label: "Inhalt-Datei (Pfad/Referenz)", kind: "text" },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-schulungen"
        emptyText="Keine Schulungen im Katalog."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neue Schulung"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-trainings"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Schulung bearbeiten" : "Neue Schulung"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          intervalYears:
            crud.dialog.row?.intervalYears != null ? String(crud.dialog.row.intervalYears) : "",
          url: crud.dialog.row?.url ?? "",
          contentFile: crud.dialog.row?.contentFile ?? "",
        }}
        pending={crud.save.isPending}
        onSubmit={(v: FormValues) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: {
              name: String(v.name).trim(),
              intervalYears: v.intervalYears ? Number(v.intervalYears) : null,
              url: String(v.url).trim() || null,
              contentFile: String(v.contentFile).trim() || null,
            },
          })
        }
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Tab 2: Fälligkeiten (Mitarbeiterschulungen)
// ---------------------------------------------------------------------------

const EXPIRING_CHIPS: { label: string; value: number | null }[] = [
  { label: "30 Tage", value: 30 },
  { label: "90 Tage", value: 90 },
  { label: "180 Tage", value: 180 },
  { label: "Alles", value: null },
]

function DueTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const queryClient = useQueryClient()

  // Default-Filter: Ablauf in ≤ 90 Tagen, nur gültige, nur aktive MA (G-6)
  const [expiringWithin, setExpiringWithin] = useState<number | null>(90)
  const [validOnly, setValidOnly] = useState(true)
  const [activeOnly, setActiveOnly] = useState(true)
  const [employeeFilter, setEmployeeFilter] = useState("")

  const [dialog, setDialog] = useState<{ open: boolean; row: TrainingRecord | null }>({
    open: false,
    row: null,
  })

  // Nachweis-Upload: verstecktes File-Input, Ziel-ID im Ref (Muster SickLeavesTab)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadTargetRef = useRef<string | null>(null)

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
  })
  const courses = useQuery({
    queryKey: ["training-courses"],
    queryFn: () => api.trainings.courses.list({ size: 100 }),
  })

  const params = {
    size: 100,
    employee: employeeFilter ? Number(employeeFilter) : undefined,
    valid: validOnly ? true : undefined,
    expiringWithin: expiringWithin ?? undefined,
    activeOnly: activeOnly || undefined,
  }
  const records = useQuery({
    queryKey: ["training-records", params],
    queryFn: () => api.trainings.records.list(params),
    placeholderData: keepPreviousData,
  })

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["training-records"] })
  }

  const save = useMutation({
    mutationFn: ({ id, values }: { id: string | null; values: FormValues }) => {
      const base = {
        trainingCourseId: Number(values.trainingCourseId),
        trainedFrom: String(values.trainedFrom) || null,
        trainedTo: String(values.trainedTo) || null,
        examDate: String(values.examDate) || null,
        examStatus: values.examStatus as PruefungStatus,
        valid: values.valid === true,
        expiryDate: String(values.expiryDate) || null,
        note: String(values.note).trim() || null,
      }
      return id
        ? api.trainings.records.update(id, base)
        : api.trainings.records.create({ ...base, employeeId: Number(values.employeeId) })
    },
    onSuccess: (_data, vars) => {
      invalidate()
      toast.success(vars.id ? "Schulungsnachweis aktualisiert" : "Schulungsnachweis angelegt")
      setDialog({ open: false, row: null })
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const renew = useMutation({
    mutationFn: (id: string) => api.trainings.records.renew(id),
    onSuccess: (created) => {
      invalidate()
      toast.success("Folgedatensatz angelegt — bitte neue Zeiträume erfassen")
      // G-8: den frischen (leeren) Folgedatensatz direkt zum Bearbeiten öffnen
      setDialog({ open: true, row: created })
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const upload = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      api.trainings.records.uploadCertificate(id, file),
    onSuccess: () => {
      invalidate()
      toast.success("Nachweis hochgeladen")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.trainings.records.remove(id),
    onSuccess: () => {
      invalidate()
      toast.success("Schulungsnachweis gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function startUpload(id: string) {
    uploadTargetRef.current = id
    fileInputRef.current?.click()
  }

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    const id = uploadTargetRef.current
    if (file && id) upload.mutate({ id, file })
    e.target.value = ""
    uploadTargetRef.current = null
  }

  const rows = records.data?.data ?? []
  const editing = dialog.row

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<TrainingRecord>[]>(
    () => [
      {
        id: "employee",
        header: "Mitarbeiter",
        accessorFn: (r) => r.employeeName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "course",
        header: "Schulung",
        accessorFn: (r) => r.courseName,
      },
      {
        id: "period",
        // ISO-String als Accessor sortiert korrekt
        header: "Zeitraum",
        accessorFn: (r) => r.trainedFrom ?? "",
        cell: ({ row }) =>
          row.original.trainedFrom || row.original.trainedTo
            ? `${fmtDate(row.original.trainedFrom)} – ${fmtDate(row.original.trainedTo)}`
            : "—",
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "exam",
        header: "Prüfung",
        accessorFn: (r) => EXAM_STATUS[r.examStatus].label,
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5">
            <Badge variant="outline" className={EXAM_STATUS[row.original.examStatus].badge}>
              {EXAM_STATUS[row.original.examStatus].label}
            </Badge>
            {row.original.examDate && (
              <span className="text-xs text-muted-foreground tabular-nums">
                {fmtDate(row.original.examDate)}
              </span>
            )}
          </span>
        ),
      },
      {
        id: "expiry",
        header: "Ablauf",
        accessorFn: (r) => r.expiryDate ?? "",
        cell: ({ row }) => <DueBadge date={row.original.expiryDate} />,
      },
      {
        id: "certificate",
        header: "Nachweis",
        // Boolescher Wert als Zahl → sortierbar
        accessorFn: (r) => (r.certificateFile ? 1 : 0),
        cell: ({ row }) =>
          row.original.certificateFile ? (
            <FileCheck2 className="size-4 text-green-700" aria-label="Nachweis vorhanden" />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      ...(canWrite || canDelete
        ? ([
            {
              id: "actions",
              header: "Aktionen",
              enableSorting: false,
              enableHiding: false,
              meta: { headClassName: "w-36 text-right", cellClassName: "text-right" },
              cell: ({ row }) => (
                <div className="flex justify-end gap-1">
                  {canWrite && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        aria-label="Erneuern"
                        title="Erneuern (Folgedatensatz anlegen)"
                        disabled={renew.isPending}
                        onClick={() => renew.mutate(row.original.id)}
                      >
                        <RefreshCw className="!size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        aria-label="Nachweis hochladen"
                        title="Nachweis hochladen"
                        disabled={upload.isPending}
                        onClick={() => startUpload(row.original.id)}
                      >
                        <Paperclip className="!size-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        aria-label="Bearbeiten"
                        onClick={() => setDialog({ open: true, row: row.original })}
                      >
                        <Pencil className="!size-3.5" />
                      </Button>
                    </>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-destructive hover:text-destructive"
                      aria-label="Löschen"
                      disabled={del.isPending}
                      onClick={() => {
                        if (window.confirm("Schulungsnachweis wirklich löschen?"))
                          del.mutate(row.original.id)
                      }}
                    >
                      <Trash2 className="!size-3.5" />
                    </Button>
                  )}
                </div>
              ),
            },
          ] satisfies ColumnDef<TrainingRecord>[])
        : []),
    ],
    // renew/upload/del sind stabile Mutation-Referenzen; startUpload nutzt nur Refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, canDelete, renew.isPending, upload.isPending, del.isPending],
  )

  // Beim Bearbeiten ist der Mitarbeiter fix (Update-Schema ohne employeeId)
  const fields: FieldDef[] = [
    ...(editing
      ? []
      : ([
          {
            key: "employeeId",
            label: "Mitarbeiter",
            kind: "select",
            required: true,
            emptyOption: "— wählen —",
            options: (employees.data?.data ?? []).map((e) => ({
              value: e.id,
              label: e.displayName,
            })),
          },
        ] satisfies FieldDef[])),
    {
      key: "trainingCourseId",
      label: "Schulung",
      kind: "select",
      required: true,
      emptyOption: "— wählen —",
      options: (courses.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    },
    { key: "trainedFrom", label: "Geschult von", kind: "date" },
    { key: "trainedTo", label: "Geschult bis", kind: "date" },
    { key: "examDate", label: "Prüfungsdatum", kind: "date" },
    {
      key: "examStatus",
      label: "Prüfstatus",
      kind: "select",
      required: true,
      options: (Object.keys(EXAM_STATUS) as PruefungStatus[]).map((s) => ({
        value: s,
        label: EXAM_STATUS[s].label,
      })),
    },
    { key: "valid", label: "Gültig", kind: "switch" },
    { key: "expiryDate", label: "Ablaufdatum", kind: "date" },
    { key: "note", label: "Bemerkung", kind: "textarea" },
  ]

  return (
    <div className="space-y-3">
      {/* Filterleiste */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Ablauf innerhalb</Label>
            <div className="flex gap-1">
              {EXPIRING_CHIPS.map((chip) => (
                <button
                  key={chip.label}
                  type="button"
                  onClick={() => setExpiringWithin(chip.value)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    expiringWithin === chip.value
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-input bg-transparent text-muted-foreground hover:bg-accent"
                  )}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
          <div className="w-56 space-y-1.5">
            <Label>Mitarbeiter</Label>
            <Select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
              <option value="">Alle Mitarbeiter</option>
              {(employees.data?.data ?? []).map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.displayName}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex h-9 items-center gap-2">
            <Switch checked={validOnly} onCheckedChange={setValidOnly} />
            <Label className="font-normal">Nur gültige</Label>
          </div>
          <div className="flex h-9 items-center gap-2">
            <Switch checked={activeOnly} onCheckedChange={setActiveOnly} />
            <Label className="font-normal">Nur aktive MA</Label>
          </div>
        </div>
        {canWrite && (
          <Button onClick={() => setDialog({ open: true, row: null })}>
            <Plus /> Neuer Nachweis
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="py-2">
          {records.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="trainings"
              emptyMessage="Keine Schulungsnachweise für die gewählten Filter."
            />
          )}
        </CardContent>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="application/pdf,image/*"
        onChange={onFileSelected}
      />

      <EntityFormSheet
        sheetKey="entity-form-trainings"
        open={dialog.open}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
        title={editing ? `Nachweis bearbeiten — ${editing.employeeName}` : "Neuer Schulungsnachweis"}
        fields={fields}
        initialValues={{
          employeeId: editing?.employeeId ?? "",
          trainingCourseId: editing?.trainingCourseId ?? "",
          trainedFrom: editing?.trainedFrom ?? "",
          trainedTo: editing?.trainedTo ?? "",
          examDate: editing?.examDate ?? "",
          examStatus: editing?.examStatus ?? "FEHLT",
          valid: editing?.valid ?? false,
          expiryDate: editing?.expiryDate ?? "",
          note: editing?.note ?? "",
        }}
        pending={save.isPending}
        onSubmit={(values) => save.mutate({ id: editing?.id ?? null, values })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

export function TrainingsPage() {
  const canWrite = useCan("schulung_schreiben")
  const canDelete = useCan("loeschen")
  const [tab, setTab] = useState("faelligkeiten")

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Schulungen</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="faelligkeiten">Fälligkeiten</TabsTrigger>
          <TabsTrigger value="katalog">Katalog</TabsTrigger>
        </TabsList>
        <TabsContent value="faelligkeiten">
          <DueTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="katalog">
          <CatalogTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
